import React, { useEffect, useState, FormEvent } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import AdminGuard from '../components/AdminGuard';
import AdminLayout from '../components/AdminLayout';
import { api } from '../../../api/client';
import { Schedule } from '@thekwallace/backtime-shared';

interface TimelineOwner {
  id: string;
  displayName?: string;
  email: string;
}

interface Timeline {
  id: string;
  name: string;
  shareCode?: string;
  schedule?: Schedule;
  isAutoAdvanceEnabled: boolean;
  isPublic: boolean;
  owner?: TimelineOwner;
  createdAt: string;
  _count?: {
    subscribers: number;
  };
}

interface TimelineStats {
  totalTimelines: number;
  withSchedule?: number;
  autoAdvanceEnabled?: number;
  publicTimelines?: number;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface TimelinesResponse {
  timelines: Timeline[];
  pagination: Pagination;
}

const AdminTimelinesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [timelines, setTimelines] = useState<Timeline[]>([]);
  const [stats, setStats] = useState<TimelineStats | null>(null);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [search, setSearch] = useState(searchParams.get('search') || '');

  const fetchTimelines = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: String(page), limit: '50' });
      if (search) params.append('search', search);

      const [timelinesResponse, statsResponse] = await Promise.all([
        api.get(`/admin/timelines?${params}`) as Promise<TimelinesResponse>,
        api.get('/admin/timelines/stats') as Promise<TimelineStats>,
      ]);

      setTimelines(timelinesResponse.timelines);
      setPagination(timelinesResponse.pagination);
      setStats(statsResponse);
    } catch (err) {
      const apiError = err as { message?: string };
      setError(apiError.message || 'Failed to load timelines');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimelines();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    setSearchParams(params);
    fetchTimelines(1);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getScheduleCount = (schedule?: Schedule): number => {
    if (!schedule) return 0;
    return Object.values(schedule).flat().length;
  };

  return (
    <AdminGuard>
      <AdminLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Timelines</h2>
            {stats && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {stats.totalTimelines} total timelines
              </span>
            )}
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                  {stats.totalTimelines}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">With Schedule</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                  {stats.withSchedule || 0}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Auto-Advance</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                  {stats.autoAdvanceEnabled || 0}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Public</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                  {stats.publicTimelines || 0}
                </div>
              </div>
            </div>
          )}

          {/* Search */}
          <form onSubmit={handleSearch} className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or share code..."
                  className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Search
              </button>
            </div>
          </form>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 p-4 rounded-lg">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500 dark:text-gray-400">Loading...</div>
            </div>
          ) : (
            <>
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Timeline
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Owner
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Schedule
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Subscribers
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Settings
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {timelines.map((timeline) => (
                      <tr key={timeline.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {timeline.name}
                            </div>
                            {timeline.shareCode && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                /{timeline.shareCode}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {timeline.owner ? (
                            <Link
                              to={`/admin/users/${timeline.owner.id}`}
                              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                            >
                              {timeline.owner.displayName || timeline.owner.email}
                            </Link>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {getScheduleCount(timeline.schedule)} times
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {timeline._count?.subscribers || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex gap-2">
                            {timeline.isAutoAdvanceEnabled && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 rounded">
                                Auto
                              </span>
                            )}
                            {timeline.isPublic && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 rounded">
                                Public
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(timeline.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => fetchTimelines(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => fetchTimelines(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </AdminLayout>
    </AdminGuard>
  );
};

export default AdminTimelinesPage;
